import type { MatrixClient, User } from "matrix-js-sdk";
import { SetPresence, UserEvent } from "matrix-js-sdk";
import type { Writable } from "svelte/store";
import { derived, readable, writable } from "svelte/store";
import { AvailabilityStatus } from "@workadventure/messages";
import type { ChatUser } from "../ChatConnection";
import { createMatrixUserAvatarPictureStore } from "./MatrixMediaResolver";
import { gameManager } from "../../../Phaser/Game/GameManager";
import { currentPlayerWokaStore } from "../../../Stores/CurrentPlayerWokaStore";
import type { PictureStore } from "../../../Stores/PictureStore";
import { getColorByString } from "../../../Utils/ColorGenerator";

const LIVE_PICTURE_STORE_REFRESH_INTERVAL_MS = 8000;
const cachedUsersByClient = new WeakMap<MatrixClient, Map<string, CachedChatUser>>();
const refreshSignalsByClient = new WeakMap<MatrixClient, ClientRefreshSignal>();

type CachedChatUser = {
    user: ChatUser;
    setAvailabilityStatus: (status: AvailabilityStatus) => void;
};

type ClientRefreshSignal = {
    tickStore: Writable<number>;
    notify: () => void;
};

export const chatUserFactory: (matrixChatUser: User, matrixClient: MatrixClient) => ChatUser = (
    matrixChatUser,
    matrixClient
) => {
    let cachedUsers = cachedUsersByClient.get(matrixClient);
    if (!cachedUsers) {
        cachedUsers = new Map<string, CachedChatUser>();
        cachedUsersByClient.set(matrixClient, cachedUsers);
    }

    const existing = cachedUsers.get(matrixChatUser.userId);
    if (existing) {
        existing.user.username = matrixChatUser.rawDisplayName;
        existing.setAvailabilityStatus(mapMatrixPresenceToAvailabilityStatus(matrixChatUser.presence));
        return existing.user;
    }

    const matrixAvatarStore = createMatrixUserAvatarPictureStore(matrixClient, matrixChatUser, 48);
    const isCurrentUser = matrixClient.getSafeUserId?.() === matrixChatUser.userId;
    const resolvedPictureStore = isCurrentUser
        ? mergePictureStores(currentPlayerWokaStore, matrixAvatarStore)
        : createLivePreferredPictureStore(matrixClient, matrixChatUser, matrixAvatarStore);
    const availabilityStatusStore = writable(mapMatrixPresenceToAvailabilityStatus(matrixChatUser.presence));

    const createdUser: ChatUser = {
        chatId: matrixChatUser.userId,
        username: matrixChatUser.rawDisplayName,
        roomName: undefined,
        playUri: undefined,
        pictureStore: resolvedPictureStore,
        color: getColorByString(matrixChatUser.userId) ?? undefined,
        spaceUserId: undefined,
        availabilityStatus: availabilityStatusStore,
    };

    cachedUsers.set(matrixChatUser.userId, {
        user: createdUser,
        setAvailabilityStatus: (status) => availabilityStatusStore.set(status),
    });

    return createdUser;
};

function getOrCreateClientRefreshSignal(matrixClient: MatrixClient): ClientRefreshSignal {
    const existing = refreshSignalsByClient.get(matrixClient);
    if (existing) {
        return existing;
    }

    const tickStore = writable(0);
    const notify = () => {
        tickStore.update((value) => value + 1);
    };

    const onPresenceChange = () => {
        notify();
    };

    matrixClient.on(UserEvent.Presence, onPresenceChange);
    globalThis.setInterval(notify, LIVE_PICTURE_STORE_REFRESH_INTERVAL_MS);

    const created: ClientRefreshSignal = { tickStore, notify };
    refreshSignalsByClient.set(matrixClient, created);
    return created;
}

function createLivePreferredPictureStore(matrixClient: MatrixClient, matrixChatUser: User, fallback: PictureStore): PictureStore {
    return readable<string | undefined>(undefined, (set) => {
        let latestLivePicture: string | undefined;
        let latestFallbackPicture: string | undefined;
        let livePictureStore: PictureStore | undefined;
        let livePictureUnsubscriber: (() => void) | undefined;
        const refreshSignal = getOrCreateClientRefreshSignal(matrixClient);

        const applyPicture = () => {
            set(latestLivePicture ?? latestFallbackPicture);
        };

        const attachLivePictureStore = (nextStore: PictureStore | undefined) => {
            if (nextStore === livePictureStore) {
                return;
            }

            if (livePictureUnsubscriber) {
                livePictureUnsubscriber();
                livePictureUnsubscriber = undefined;
            }

            livePictureStore = nextStore;

            if (nextStore) {
                livePictureUnsubscriber = nextStore.subscribe((picture) => {
                    latestLivePicture = picture;
                    applyPicture();
                });
            } else {
                latestLivePicture = undefined;
                applyPicture();
            }
        };

        const refreshLivePictureStore = () => {
            attachLivePictureStore(resolveOnlineUserPictureStore(matrixChatUser));
        };

        const fallbackUnsubscriber = fallback.subscribe((picture) => {
            latestFallbackPicture = picture;
            applyPicture();
        });

        const refreshSignalUnsubscriber = refreshSignal.tickStore.subscribe(() => {
            refreshLivePictureStore();
        });

        refreshLivePictureStore();

        return () => {
            if (livePictureUnsubscriber) {
                livePictureUnsubscriber();
            }
            fallbackUnsubscriber();
            refreshSignalUnsubscriber();
        };
    });
}

function mergePictureStores(primary: PictureStore, fallback: PictureStore): PictureStore {
    return derived([primary, fallback], ([$primary, $fallback]) => $primary ?? $fallback);
}

function resolveOnlineUserPictureStore(matrixChatUser: User) {
    try {
        const scene = gameManager.getCurrentGameScene();
        const repository = scene.getRemotePlayersRepository();
        const remotePlayerData =
            repository.getPlayerByChatId(matrixChatUser.userId) ??
            Array.from(repository.getPlayers().values()).find(
                (player) => normalizeName(player.name) === normalizeName(matrixChatUser.rawDisplayName)
            );

        if (!remotePlayerData) return undefined;
        const remotePlayer = scene.MapPlayersByKey.get(remotePlayerData.userId);
        return remotePlayer?.pictureStore;
    } catch {
        return undefined;
    }
}

function normalizeName(name: string | null | undefined): string {
    return (name ?? "").trim().toLowerCase();
}

export function mapMatrixPresenceToAvailabilityStatus(presence: string = SetPresence.Offline): AvailabilityStatus {
    switch (presence) {
        case SetPresence.Offline:
            return AvailabilityStatus.UNCHANGED;
        case SetPresence.Online:
            return AvailabilityStatus.ONLINE;
        case SetPresence.Unavailable:
            return AvailabilityStatus.AWAY;
        //TODO : use SetPresence.Busy after matrix-js-sdk update
        //case SetPresence.Busy:
        case "busy":
            return AvailabilityStatus.BUSY;
        default:
            console.error(`Do not handle the status ${presence}`);
            return AvailabilityStatus.UNCHANGED;
    }
}
